import type { SportIconName } from '@/ui';
import type { CategoryMenuItem, SportMenuItem, TournamentMenuItem } from '@/api/queries';
import { crestUrl, flagUrl, isFlagAssetUrl } from './flag-assets';

/** Convert an API display name into a URL slug. */
export function toSlug(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-');
}

export interface ResolvedMenuPath {
  sport: SportMenuItem;
  category: CategoryMenuItem;
  tournament: TournamentMenuItem;
}

export interface ResolvedCategoryPath {
  sport: SportMenuItem;
  category: CategoryMenuItem;
}

/** Resolve URL slugs back to menu entities (API filter names use display `Name`). */
export function resolveMenuPath(
  sports: SportMenuItem[] | undefined,
  sportSlug: string,
  categorySlug: string,
  tournamentSlug: string,
): ResolvedMenuPath | null {
  const categoryPath = resolveCategoryPath(sports, sportSlug, categorySlug);
  if (!categoryPath) return null;

  const tournament = categoryPath.category.Tournaments?.find(
    (item) => toSlug(item.Name) === tournamentSlug,
  );
  if (!tournament) return null;

  return { ...categoryPath, tournament };
}

/** Resolve a sport slug to its menu entity (API filter names use display `Name`). */
export function resolveSport(
  sports: SportMenuItem[] | undefined,
  sportSlug: string,
): SportMenuItem | null {
  if (!sports?.length) return null;
  return sports.find((item) => toSlug(item.Name) === sportSlug) ?? null;
}

/** Resolve sport + category slugs for category-wide event lists. */
export function resolveCategoryPath(
  sports: SportMenuItem[] | undefined,
  sportSlug: string,
  categorySlug: string,
): ResolvedCategoryPath | null {
  const sport = resolveSport(sports, sportSlug);
  if (!sport) return null;

  const category = sport.Categories?.find((item) => toSlug(item.Name) === categorySlug);
  if (!category) return null;

  return { sport, category };
}

export interface MenuExpansionIds {
  sportId: number;
  categoryId: number | null;
}

/**
 * Resolve SideNav accordion expansion from URL slugs.
 * Returns sport (+ category when present) so the rail can stay open on deep links.
 */
export function resolveMenuExpansion(
  sports: SportMenuItem[] | undefined,
  sportSlug: string | undefined,
  categorySlug?: string | undefined,
  tournamentSlug?: string | undefined,
): MenuExpansionIds | null {
  if (!sportSlug) return null;

  if (categorySlug && tournamentSlug) {
    const path = resolveMenuPath(sports, sportSlug, categorySlug, tournamentSlug);
    if (path) return { sportId: path.sport.Id, categoryId: path.category.Id };
  }

  if (categorySlug) {
    const path = resolveCategoryPath(sports, sportSlug, categorySlug);
    if (path) return { sportId: path.sport.Id, categoryId: path.category.Id };
  }

  const sport = resolveSport(sports, sportSlug);
  if (!sport) return null;

  return { sportId: sport.Id, categoryId: null };
}

/**
 * Build the sports route target when switching sport while preserving
 * Last Minute / Top Events / Today (or falling back to prematch landing).
 * Clears category/tournament deep links by targeting sport-level routes.
 */
export function sportChangeTarget(pathname: string, sportSlug: string) {
  if (pathname.includes('/lastminute')) {
    return { to: '/sports/$sport/lastminute' as const, params: { sport: sportSlug } };
  }
  if (pathname.includes('/topevents')) {
    return { to: '/sports/$sport/topevents' as const, params: { sport: sportSlug } };
  }
  if (pathname.includes('/today')) {
    return { to: '/sports/$sport/today' as const, params: { sport: sportSlug } };
  }
  return { to: '/sports/$sport' as const, params: { sport: sportSlug } };
}

export function normalizeLookupKey(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Maps API category name → ISO 3166-1 alpha-2 flag code.
 * Keys are lowercased for case-insensitive lookup.
 */
const CATEGORY_FLAG_MAP: Record<string, string> = {
  // Football countries
  england: 'gb-eng',
  scotland: 'gb-sct',
  wales: 'gb-wls',
  'northern ireland': 'gb-nir',
  germany: 'de',
  deutschland: 'de',
  france: 'fr',
  spain: 'es',
  italy: 'it',
  netherlands: 'nl',
  portugal: 'pt',
  turkey: 'tr',
  turkiye: 'tr',
  türkiye: 'tr',
  russia: 'ru',
  ukraine: 'ua',
  belgium: 'be',
  croatia: 'hr',
  serbia: 'rs',
  austria: 'at',
  switzerland: 'ch',
  greece: 'gr',
  denmark: 'dk',
  norway: 'no',
  sweden: 'se',
  finland: 'fi',
  poland: 'pl',
  czechia: 'cz',
  'czech republic': 'cz',
  slovakia: 'sk',
  slovenia: 'si',
  iceland: 'is',
  belarus: 'by',
  estonia: 'ee',
  'bosnia and herzegovina': 'ba',
  'bosnia & herzegovina': 'ba',
  lithuania: 'lt',
  latvia: 'lv',
  georgia: 'ge',
  georgiam: 'ge',
  kazakhstan: 'kz',
  kazkhstan: 'kz',
  hungary: 'hu',
  romania: 'ro',
  bulgaria: 'bg',
  'north macedonia': 'mk',
  albania: 'al',
  israel: 'il',
  ireland: 'ie',
  'republic of ireland': 'ie',
  argentina: 'ar',
  brazil: 'br',
  colombia: 'co',
  chile: 'cl',
  uruguay: 'uy',
  peru: 'pe',
  paraguay: 'py',
  venezuela: 've',
  ecuador: 'ec',
  'costa rica': 'cr',
  honduras: 'hn',
  'el salvador': 'sv',
  bolivia: 'bo',
  mexico: 'mx',
  usa: 'us',
  'united states': 'us',
  canada: 'ca',
  japan: 'jp',
  'south korea': 'kr',
  'republic of korea': 'kr',
  korea: 'kr',
  china: 'cn',
  india: 'in',
  armenia: 'am',
  azerbaijan: 'az',
  cyprus: 'cy',
  malaysia: 'my',
  iran: 'ir',
  'islamic republic of iran': 'ir',
  algeria: 'dz',
  iraq: 'iq',
  jordan: 'jo',
  kuwait: 'kw',
  uzbekistan: 'uz',
  australia: 'au',
  'new zealand': 'nz',
  'saudi arabia': 'sa',
  uae: 'ae',
  'united arab emirates': 'ae',
  qatar: 'qa',
  morocco: 'ma',
  egypt: 'eg',
  nigeria: 'ng',
  'south africa': 'za',
  kenya: 'ke',
  tunisia: 'tn',
  tanzania: 'tz',
  zanzibar: 'tz',
  zimbabwe: 'zw',
  uganda: 'ug',
  oman: 'om',
  singapore: 'sg',
  malta: 'mt',
  moldova: 'md',
  'republic of moldova': 'md',
  bahrain: 'bh',
  bahrein: 'bh',
  indonesia: 'id',
  montenegro: 'me',
  thailand: 'th',
  vietnam: 'vn',
  myanmar: 'mm',
  lebanon: 'lb',
  luxembourg: 'lu',
  'hong kong': 'hk',
  international: 'un',
  'international clubs': 'un',
  europe: 'un',
  world: 'world',
};

export function getCategoryFlag(categoryName: string): string | undefined {
  const key = normalizeLookupKey(categoryName);
  const directCode = CATEGORY_FLAG_MAP[key];
  if (directCode) {
    return flagUrl(directCode);
  }

  // Handle suffix-based labels like "England Amateur".
  const suffixes = [' amateur'];
  for (const suffix of suffixes) {
    if (key.endsWith(suffix)) {
      const baseKey = key.slice(0, -suffix.length).trim();
      const baseCode = CATEGORY_FLAG_MAP[baseKey];
      if (baseCode) {
        return flagUrl(baseCode);
      }
    }
  }

  return undefined;
}

/**
 * Maps tournament crest assets. Keys are `category::tournament` (both normalized),
 * or `*::tournament` for globally unique competitions that are safe without a country.
 *
 * Country-scoped leagues (Premier League, Serie A, Superliga, …) MUST include the
 * category so identically named tournaments in other countries do not reuse the crest.
 */
const TOURNAMENT_CREST_MAP: Record<string, string> = {
  // England
  'england::premier league': 'premierleague',
  'england::efl cup': 'eflcup',
  'england::carabao cup': 'eflcup',

  // Spain
  'spain::la liga': 'laliga',
  'spain::laliga': 'laliga',
  'spain::la liga 2': 'laliga2',

  // Italy
  'italy::serie a': 'seriea',

  // Germany
  'germany::bundesliga': 'bundesliga',
  'deutschland::bundesliga': 'bundesliga',

  // France
  'france::ligue 1': 'ligue1',

  // Netherlands
  'netherlands::eredivisie': 'eredivisie',

  // Turkey
  'turkey::super lig': 'superlig',
  'turkey::superlig': 'superlig',
  'turkiye::super lig': 'superlig',
  'türkiye::super lig': 'superlig',
  'turkey::1. lig': '1.lig',
  'turkiye::1. lig': '1.lig',

  // Nordics
  'denmark::superliga': 'superliga',
  'norway::eliteserien': 'eliteserien',
  'sweden::allsvenskan': 'allsvenskan',

  // Americas
  'usa::nba': 'nba',
  'united states::nba': 'nba',
  'usa::nhl': 'nhl',
  'united states::nhl': 'nhl',
  'usa::mls': 'mls',
  'united states::mls': 'mls',
  'usa::major league soccer': 'majorleaguesoccer',
  'united states::major league soccer': 'majorleaguesoccer',
  'mexico::liga mx': 'ligamx',
  'brazil::brasileirao serie a': 'brasileiroseriea',
  'brazil::brasileiro serie a': 'brasileiroseriea',

  // Asia
  'japan::j league': 'jleague',
  'japan::j.league': 'jleague',
  'china::chinese super league': 'chinesesuperleague',

  // Belgium
  'belgium::pro league': 'proleague',

  // International / unique competitions (safe without country)
  '*::champions league': 'uefachampionsleague',
  '*::uefa champions league': 'uefachampionsleague',
  '*::europa league': 'uefaeuropaleague',
  '*::uefa europa league': 'uefaeuropaleague',
  '*::conference league': 'uefaeuropaconferenceleague',
  '*::europa conference league': 'uefaeuropaconferenceleague',
  '*::uefa europa conference league': 'uefaeuropaconferenceleague',
  '*::nations league': 'uefanationsleague',
  '*::uefa nations league': 'uefanationsleague',
  '*::super cup': 'uefasupercup',
  '*::uefa super cup': 'uefasupercup',
  '*::copa libertadores': 'copalibertadores',
  '*::copa sudamericana': 'copasudamericana',
  '*::concacaf gold cup': 'concacafgoldcup',
  '*::fifa club world cup': 'fifaclubworldcup',
  '*::euroleague': 'euroleague',
  '*::nba': 'nba',
  '*::nhl': 'nhl',
  '*::rugby union': 'rugbyunion',
  '*::rugby league': 'rugbyleague',
};

function tournamentCrestLookupKey(tournamentName: string, categoryName?: string | null): string {
  const tournamentKey = normalizeLookupKey(tournamentName);
  const categoryKey = categoryName ? normalizeLookupKey(categoryName) : '';
  return categoryKey ? `${categoryKey}::${tournamentKey}` : `*::${tournamentKey}`;
}

/**
 * Resolve a tournament crest. Always pass `categoryName` for country leagues so
 * shared names (e.g. Premier League) do not resolve to the wrong crest.
 */
export function getTournamentCrest(
  tournamentName: string,
  categoryName?: string | null,
): string | undefined {
  const tournamentKey = normalizeLookupKey(tournamentName);
  if (!tournamentKey) return undefined;

  if (categoryName) {
    const scopedKey = tournamentCrestLookupKey(tournamentName, categoryName);
    const scopedCrest = TOURNAMENT_CREST_MAP[scopedKey];
    if (scopedCrest) return crestUrl(scopedCrest);
  }

  const unscopedCrest = TOURNAMENT_CREST_MAP[`*::${tournamentKey}`];
  return unscopedCrest ? crestUrl(unscopedCrest) : undefined;
}

/** Crest if known for this category+tournament, otherwise the category flag. */
export function getTournamentOrCategoryBadge(
  tournamentName: string | null | undefined,
  categoryName: string | null | undefined,
): string | undefined {
  if (tournamentName) {
    const crest = getTournamentCrest(tournamentName, categoryName);
    if (crest) return crest;
  }
  if (categoryName) return getCategoryFlag(categoryName);
  return undefined;
}

/** True when a badge URL is a category flag (vs a tournament crest). */
export function isFlagBadgeSrc(src: string | null | undefined): boolean {
  return Boolean(src && isFlagAssetUrl(src));
}

/** Maps API sport names to design-system icon keys. Falls back to `football`. */
const SPORT_ICON_MAP: Record<string, SportIconName> = {
  football: 'football',
  basketball: 'basketball',
  tennis: 'tennis',
  baseball: 'baseball',
  volleyball: 'volleyball',
  handball: 'handball',
  rugby: 'rugby',
  boxing: 'boxing',
  darts: 'darts',
  esports: 'esports',
  futsal: 'futsal',
  'ice-hockey': 'icehockey',
  'american-football': 'americanfootball',
  'table-tennis': 'tabletennis',
  hockey: 'hockey',
};

export function sportIcon(slug: string): SportIconName {
  return SPORT_ICON_MAP[slug] ?? 'football';
}
